/* Heal trips route learning never saw — from the console, in the browser.
 *
 * Bug 135 (2026-08-22): release builds seal every trail point and the finish
 * path read them plain, so every real-phone trip since 2026-08-10 finished with
 * no start/end — no `mapStartLat`, no ri* stamp, `riSkipReason: no_endpoints`.
 * The trail IS on the trip document, sealed. The phone-side heal (+84) could
 * not list the trips (the driver read rule cannot be proved for a query), so
 * the platform admin does it here instead: the dashboard already holds the
 * location key (the Live map decrypts with it), `isAdmin()` may read and update
 * any attendance row, and the backend can rebuild the org's routes on demand.
 *
 * Same anchors a live finish would have chosen — first pickup / last drop when
 * the run has them, else the trail's first / last readable point. Idempotent:
 * a trip that already carries ri* is skipped; the learner de-dupes by trip id.
 */

import { deleteField, doc, getDoc, serverTimestamp, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { ensureLocationDek, openLatLng } from '../utils/locationCrypto';
import type { RouteLearningStatusTrip } from '../services/consoleApi';

type LL = { lat: number; lng: number };

async function firstOpenable(points: unknown[], fromStart: boolean): Promise<LL | null> {
  const n = points.length;
  const limit = Math.min(n, 8);
  for (let i = 0; i < limit; i++) {
    const p = fromStart ? points[i] : points[n - 1 - i];
    // eslint-disable-next-line no-await-in-loop
    const ll = await openLatLng(p);
    if (ll && Number.isFinite(ll.lat) && Number.isFinite(ll.lng)) return { lat: ll.lat, lng: ll.lng };
  }
  return null;
}

function childTs(c: Record<string, unknown>): number {
  const t = c.timestamp;
  if (t && typeof (t as Timestamp).toMillis === 'function') return (t as Timestamp).toMillis();
  return 0;
}

function num(v: unknown): number | null {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

export interface HealResult {
  healed: number;
  skipped: number;
  errors: string[];
}

function metersBetween(a: LL, b: LL): number {
  const R = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2
    + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

/* Second job (2026-08-23): RE-ANCHOR morning runs. Their start used to be the
   first pickup's coordinates; Gabriel changed the rule to "where Start was
   pressed" (+89), so already-stamped morning trips carry the old anchor and
   never cluster with new ones. For a morning trip that already has ri*, open
   the trail's first point and move the start anchor there (end untouched). */
async function reanchorMorningStart(
  ref: ReturnType<typeof doc>,
  d: Record<string, unknown>,
): Promise<'healed' | 'skipped' | null> {
  const points = Array.isArray(d.routePoints) ? (d.routePoints as unknown[]) : [];
  if (points.length < 2) return 'skipped';
  const start = await firstOpenable(points, true);
  if (!start) return 'skipped';
  const cur = { lat: num(d.riStartLat), lng: num(d.riStartLng) };
  if (cur.lat != null && cur.lng != null
      && metersBetween(start, { lat: cur.lat, lng: cur.lng }) < 30) {
    return 'skipped'; // already anchored where Start was pressed
  }
  await updateDoc(ref, {
    riStartLat: start.lat,
    riStartLng: start.lng,
    mapStartLat: start.lat,
    mapStartLng: start.lng,
    riReanchoredAt: serverTimestamp(),
    riReanchoredBy: 'console',
  });
  return 'healed';
}

export async function healUnlearnableTrips(trips: RouteLearningStatusTrip[]): Promise<HealResult> {
  const out: HealResult = { healed: 0, skipped: 0, errors: [] };
  await ensureLocationDek();

  for (const t of trips) {
    const isMorning = (t.routeType || '').toLowerCase() === 'morning';
    if (t.skip !== 'no_endpoints' || (t.points ?? 0) < 2) {
      // Morning trips that ARE stamped still get the new start anchor.
      if (isMorning && (t.points ?? 0) >= 2) {
        try {
          let ref = doc(db, 'busAttendance', t.id);
          let snap = await getDoc(ref);
          if (!snap.exists()) {
            ref = doc(db, 'fleetTrips', t.id);
            snap = await getDoc(ref);
          }
          if (snap.exists()) {
            const r = await reanchorMorningStart(ref, snap.data() as Record<string, unknown>);
            if (r === 'healed') { out.healed++; continue; }
          }
        } catch (e: any) {
          out.errors.push(`${t.id}: ${e?.message || 'reanchor failed'}`);
        }
      }
      out.skipped++;
      continue;
    }
    try {
      // busAttendance first (school-suffix orgs), fleetTrips otherwise.
      let ref = doc(db, 'busAttendance', t.id);
      let snap = await getDoc(ref);
      if (!snap.exists()) {
        ref = doc(db, 'fleetTrips', t.id);
        snap = await getDoc(ref);
      }
      if (!snap.exists()) { out.skipped++; continue; }
      const d = snap.data() as Record<string, unknown>;
      if (d.riStartLat != null) { out.skipped++; continue; } // already stamped
      const points = Array.isArray(d.routePoints) ? (d.routePoints as unknown[]) : [];
      if (points.length < 2) { out.skipped++; continue; }

      const routeType = String(d.routeType ?? t.routeType ?? '').trim();
      const rt = routeType.toLowerCase();
      const children = (Array.isArray(d.children) ? d.children : [])
        .filter((c): c is Record<string, unknown> => !!c && typeof c === 'object')
        .sort((a, b) => childTs(a) - childTs(b));

      // Start = where the driver pressed Start (trail's first point), for
      // morning runs too — mirrors the phone since 2026-08-23 (Gabriel:
      // "morning routes should anchor when you press start").
      let start: LL | null = null;
      let end: LL | null = null;
      if (rt === 'evening') {
        const drops = children.filter((c) => num(c.dropLat) != null && num(c.dropLng) != null);
        const last = drops[drops.length - 1];
        if (last) end = { lat: num(last.dropLat)!, lng: num(last.dropLng)! };
      }
      if (!start) start = await firstOpenable(points, true);
      if (!end) end = await firstOpenable(points, false);
      if (!start || !end) { out.skipped++; out.errors.push(`${t.id}: trail not readable`); continue; }

      const distanceKm = num(d.distanceKm) ?? t.distanceKm ?? 0;
      let durationMinutes = num(d.durationMinutes) ?? t.durationMinutes ?? 0;
      const started = d.routeStartedAt as Timestamp | undefined;
      const finished = d.routeFinishedAt as Timestamp | undefined;
      if ((!durationMinutes || durationMinutes <= 0) && started?.toMillis && finished?.toMillis) {
        durationMinutes = (finished.toMillis() - started.toMillis()) / 60000;
      }

      await updateDoc(ref, {
        riStartLat: start.lat,
        riStartLng: start.lng,
        riEndLat: end.lat,
        riEndLng: end.lng,
        riDurationMinutes: durationMinutes,
        riDistanceKm: distanceKm,
        riIncident: d.incidentReported === true,
        riDriverId: typeof d.driverId === 'string' ? d.driverId : '',
        riBusId: String(d.busId ?? t.busId ?? ''),
        riOrgCode: String(d.orgCode ?? d.schoolCode ?? ''),
        riRouteType: routeType,
        mapStartLat: start.lat,
        mapStartLng: start.lng,
        mapEndLat: end.lat,
        mapEndLng: end.lng,
        riSkipReason: deleteField(),
        riSkipped: deleteField(),
        riSkippedAt: deleteField(),
        riHealedAt: serverTimestamp(),
        riHealedBy: 'console',
      });
      out.healed++;
    } catch (e: any) {
      out.errors.push(`${t.id}: ${e?.message || 'failed'}`);
    }
  }
  return out;
}
